'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { useRequestResetPassword } from '@kit/supabase/hooks/use-request-reset-password';
import { Alert, AlertDescription } from '@kit/ui/alert';
import { alertExtras } from '@kit/ui/alert-extras';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';

import { useCaptcha } from '../captcha/client';
import { AuthErrorAlert } from './auth-error-alert';

const PasswordResetSchema = z.object({
  email: z.string().email(),
});

export function PasswordResetRequestContainer(params: {
  redirectPath: string;
  captchaSiteKey?: string;
}) {
  const t = useTranslations('auth');
  const resetPasswordMutation = useRequestResetPassword();
  const captcha = useCaptcha({ siteKey: params.captchaSiteKey });
  const captchaLoading = !captcha.isReady;

  const error = resetPasswordMutation.error;
  const success = resetPasswordMutation.data;

  const form = useForm({
    resolver: zodResolver(PasswordResetSchema),
    defaultValues: {
      email: '',
    },
  });

  return (
    <>
      <If condition={success}>
        <Alert className={alertExtras.success}>
          <AlertDescription>
            <Trans i18nKey={'auth.passwordResetSuccessMessage'} />
          </AlertDescription>
        </Alert>
      </If>

      <If condition={!resetPasswordMutation.data}>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(({ email }) => {
              const redirectTo = new URL(
                params.redirectPath,
                window.location.origin,
              ).href;

              return resetPasswordMutation
                .mutateAsync({
                  email,
                  redirectTo,
                  captchaToken: captcha.token,
                })
                .catch(() => {
                  captcha.reset();
                });
            })}
            className={'w-full'}
          >
            <div className={'flex flex-col gap-y-4'}>
              <AuthErrorAlert error={error} />

              <FormField
                name={'email'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey={'common.emailAddress'} />
                    </FormLabel>

                    <FormControl>
                      <Input
                        required
                        type="email"
                        placeholder={t('emailPlaceholder')}
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              {captcha.field}

              <Button
                disabled={resetPasswordMutation.isPending || captchaLoading}
                type="submit"
              >
                <If
                  condition={
                    !resetPasswordMutation.isPending && !captchaLoading
                  }
                >
                  <Trans i18nKey={'auth.passwordResetLabel'} />
                </If>

                <If condition={resetPasswordMutation.isPending}>
                  <Trans i18nKey={'auth.passwordResetLabel'} />
                </If>

                <If condition={captchaLoading}>
                  <Trans i18nKey={'auth.verifyingCaptcha'} />
                </If>
              </Button>
            </div>
          </form>
        </Form>
      </If>
    </>
  );
}
